import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiUserCheck, FiPlus, FiEdit2, FiTrash2, FiPhone, FiMapPin, FiChevronDown, FiChevronUp, FiSave, FiX, FiSearch, FiCheck } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Helper function to remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

const SuppliersManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/suppliers`);
      setSuppliers(response.data.data || []);
    } catch (error) {
      toast.error('Không thể tải danh sách NCC');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Vui lòng nhập tên NCC');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/suppliers/${editingId}`, formData);
      } else {
        await axios.post(`${API_URL}/suppliers`, formData);
      }
      loadSuppliers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi xảy ra');
    }
  };

  const handleSaveFromImport = async (supplier) => {
    try {
      await axios.post(`${API_URL}/suppliers/save-from-import`, {
        name: supplier.name
      });
      loadSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi xảy ra');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/suppliers/${id}`);
      loadSuppliers();
      setShowDeleteConfirm(null);
    } catch (error) {
      toast.error('Không thể xóa NCC');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/suppliers/${id}`)));
      setSelectedIds([]);
      loadSuppliers();
      setShowDeleteConfirm(null);
    } catch (error) {
      toast.error('Không thể xóa NCC');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const validIds = filteredSuppliers.filter(s => s._id).map(s => s._id);
    if (selectedIds.length === validIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(validIds);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', address: '', notes: '' });
    setShowAddModal(false);
    setEditingId(null);
  };

  const startEdit = (supplier) => {
    setFormData({
      name: supplier.name,
      phone: supplier.phone || '',
      address: supplier.address || '',
      notes: supplier.notes || ''
    });
    setEditingId(supplier._id);
    setShowAddModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };

  const filteredSuppliers = suppliers.filter(s => {
    const searchLower = search.toLowerCase();
    const searchNormalized = removeVietnameseDiacritics(search);
    const nameLower = (s.name || '').toLowerCase();
    const nameNormalized = removeVietnameseDiacritics(s.name);
    return nameLower.includes(searchLower) || nameNormalized.includes(searchNormalized);
  });

  const totalAmount = suppliers.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
  const totalImports = suppliers.reduce((sum, s) => sum + (s.totalImports || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 md:p-6 pb-20 md:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FiUserCheck className="text-green-600" />
          Nhà cung cấp
        </h2>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm NCC..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <FiPlus /> {!isMobile && 'Thêm'}
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-medium text-green-700">{selectedIds.length} đã chọn</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-sm text-green-600 hover:text-green-800"
            >
              Bỏ chọn
            </button>
          </div>
          <button
            onClick={() => setShowDeleteConfirm({ bulk: true })}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FiTrash2 size={16} /> Xóa
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4">
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">Tổng NCC</div>
          <div className="text-lg md:text-2xl font-bold text-blue-600">{suppliers.length}</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">Đã lưu</div>
          <div className="text-lg md:text-2xl font-bold text-green-600">
            {suppliers.filter(s => !s.isFromImport).length}
          </div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">Tổng phiếu nhập</div>
          <div className="text-lg md:text-2xl font-bold text-purple-600">{totalImports}</div>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl shadow-sm border">
          <div className="text-gray-500 text-xs md:text-sm">Tổng tiền nhập</div>
          <div className="text-base md:text-xl font-bold text-orange-600">{formatCurrency(totalAmount)}đ</div>
        </div>
      </div>

      {/* Suppliers Table */}
      {filteredSuppliers.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500 bg-white rounded-lg shadow p-8">
          <FiUserCheck size={48} className="mb-2 opacity-50" />
          <p>Chưa có nhà cung cấp nào</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {!isMobile ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === filteredSuppliers.filter(s => s._id).length && filteredSuppliers.filter(s => s._id).length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên NCC</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Liên hệ</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Phiếu nhập</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tổng tiền</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier._id || supplier.name} className={`hover:bg-gray-50 ${selectedIds.includes(supplier._id) ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-4">
                        {supplier._id && (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(supplier._id)}
                            onChange={() => toggleSelect(supplier._id)}
                            className="w-4 h-4 text-green-600 rounded"
                          />
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium">{supplier.name}</div>
                        {supplier.address && (
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <FiMapPin size={12} /> {supplier.address}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {supplier.phone ? (
                          <span className="flex items-center gap-1">
                            <FiPhone size={12} /> {supplier.phone}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right font-medium text-purple-600">
                        {supplier.totalImports || 0}
                      </td>
                      <td className="px-4 py-4 text-right font-medium text-green-600">
                        {formatCurrency(supplier.totalAmount)}đ
                      </td>
                      <td className="px-4 py-4 text-center">
                        {supplier.isFromImport ? (
                          <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                            Từ phiếu nhập
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            Đã lưu
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {supplier.isFromImport ? (
                            <button
                              onClick={() => handleSaveFromImport(supplier)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Lưu NCC"
                            >
                              <FiSave size={16} />
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => startEdit(supplier)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                title="Sửa"
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm({ id: supplier._id, name: supplier.name })}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Xóa"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setExpandedId(expandedId === (supplier._id || supplier.name) ? null : (supplier._id || supplier.name))}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                            title="Chi tiết"
                          >
                            {expandedId === (supplier._id || supplier.name) ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Mobile Cards */
            <div className="space-y-2">
              {filteredSuppliers.map((supplier) => (
                <div
                  key={supplier._id || supplier.name}
                  className={`bg-white border rounded-xl p-3 ${selectedIds.includes(supplier._id) ? 'border-green-500 bg-green-50' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {supplier._id && (
                          <div 
                            onClick={() => toggleSelect(supplier._id)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${selectedIds.includes(supplier._id) ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}
                          >
                            {selectedIds.includes(supplier._id) && <FiCheck className="text-white" size={12} />}
                          </div>
                        )}
                        <span className="font-medium">{supplier.name}</span>
                        {supplier.isFromImport && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                            Mới
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        <span>{supplier.totalImports || 0} phiếu</span>
                        <span className="text-green-600 font-medium">{formatCurrency(supplier.totalAmount)}đ</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {supplier.isFromImport ? (
                        <button
                          onClick={() => handleSaveFromImport(supplier)}
                          className="p-2 text-green-600"
                        >
                          <FiSave size={18} />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(supplier)}
                            className="p-2 text-blue-600"
                          >
                            <FiEdit2 size={18} />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm({ id: supplier._id, name: supplier.name })}
                            className="p-2 text-red-500"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">
                {editingId ? 'Sửa nhà cung cấp' : 'Thêm nhà cung cấp'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Tên NCC <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Nhập tên nhà cung cấp"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Số điện thoại</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0123 456 789"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Địa chỉ</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Địa chỉ nhà cung cấp"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows={2}
                  placeholder="Ghi chú thêm..."
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingId ? 'Cập nhật' : 'Thêm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-4">
            <h3 className="font-semibold mb-2">
              {showDeleteConfirm.bulk 
                ? `Xóa ${selectedIds.length} nhà cung cấp?` 
                : `Xóa "${showDeleteConfirm.name}"?`}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Hành động này không thể hoàn tác.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={() => showDeleteConfirm.bulk ? handleBulkDelete() : handleDelete(showDeleteConfirm.id)}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersManagement;
